import { css } from '@emotion/react'
import { ExternalStyles, Text, Theme, useTheme } from 'bold-ui'
import { Box } from 'components/Box'
import { ErrorField, RadioFieldProps, resolveName, SimNaoRadioGroupField, TextField } from 'components/form'
import { useFieldGroupTransition } from 'hooks/useFieldGroupTransition'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'

import { ConsultaSeguimentoOutroFieldModel } from '../model-consultaSeguimento'

interface PerguntaSimNaoOutroFieldProps {
  name: MetaPath<ConsultaSeguimentoOutroFieldModel>
  direction?: 'horizontal' | 'vertical'
  hideRadioOptions?: boolean
  showIndicator?: boolean
  style?: ExternalStyles
  onChange?: RadioFieldProps['onChange']
}

export function PerguntaSimNaoOutroField(props: Readonly<PerguntaSimNaoOutroFieldProps>) {
  const { name, direction = 'horizontal', hideRadioOptions, showIndicator = false, style, onChange } = props

  const { getFieldEvents, isInTransition } = useFieldGroupTransition()

  const {
    input: { value },
    meta: { error },
  } = useField<ConsultaSeguimentoOutroFieldModel>(resolveName(name), {
    subscription: { value: true, error: true },
  })

  const {
    meta: { touched: textTouched },
  } = useField<string>(resolveName(name.textValue), {
    subscription: { touched: true },
  })

  const showError = !!(textTouched && !!error?.textValue && !isInTransition(name.radioValue))

  const theme = useTheme()
  const styles = createStyles(direction, theme, showError, showIndicator)

  return (
    <Box style={styles.box}>
      <div css={[styles.root, style]}>
        <TextField
          name={name.textValue}
          label={<Text fontWeight='normal'>Outro:</Text>}
          maxLength={150}
          placeholder='Descrever, se houver'
          style={styles.textField}
          error={null}
          {...getFieldEvents(name.textValue)}
        />
        {!hideRadioOptions && (
          <div css={styles.radioWrapper}>
            <SimNaoRadioGroupField
              name={name.radioValue}
              hSpacing={0.5}
              onChange={onChange}
              disabled={isEmpty(value.textValue)}
              {...getFieldEvents(name.radioValue)}
            />
          </div>
        )}
      </div>
      {showError && <ErrorField name={name.textValue} style={styles.error} />}
    </Box>
  )
}

const createStyles = (
  direction: 'horizontal' | 'vertical',
  theme: Theme,
  hasError: boolean,
  showIndicator: boolean
) => ({
  root: css`
    display: grid;
    align-items: center;
    gap: ${direction === 'horizontal' ? '1.5rem' : '1rem'};
    ${direction === 'horizontal' &&
    `
    grid-template-columns: 1fr auto;
  `}
  `,
  textField: css`
    width: 100%;
  `,
  box: css`
    padding: 0;
    border-color: ${hasError ? theme.pallete.status.danger.main : theme.pallete.divider};
    box-shadow: ${showIndicator
      ? `-2px 0 0 0 ${theme.pallete.primary.c60}, 2px 0 0 0 ${theme.pallete.primary.c60},0  2px 0 0 ${theme.pallete.primary.c60}`
      : 'none'};
  `,
  error: css`
    padding: 0 1rem 0.75rem 1rem;
    margin-top: -0.3rem;
    display: block;
  `,
  radioWrapper: css`
    margin-top: ${direction === 'horizontal' && hasError ? '24px' : '0px'};
  `,
})
